import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { User, Mail, Phone, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { cambiarContrasena, mensajeDeErrorDeContrasena } from '../../lib/auth/changePassword';

interface MyProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  supervisor: 'Supervisor',
  executive: 'Ejecutivo',
  admin: 'Administrador',
  operator: 'Operador',
};

export function MyProfileModal({ isOpen, onClose }: MyProfileModalProps) {
  const { user, updateProfile } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pwCambiada, setPwCambiada] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && isOpen) {
      setName(user.name);
      setEmail(user.email);
      setPhone(user.phone ?? '');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setSaved(false);
      setPwCambiada(false);
      setError('');
    }
  }, [user, isOpen]);

  // Son DOS operaciones, no una, y se reportan por separado.
  //
  // Hasta el 2026-09-18 este formulario pedía la contraseña actual y la nueva,
  // las validaba… y las DESCARTABA: solo llamaba a `updateProfile`, que es
  // optimista y local. El mismo defecto del issue #76 en otra pantalla.
  //
  // Si la contraseña falla no se puede fingir que se guardó el perfil, ni al
  // revés: es el patrón que `AdminPage.handleSaveUser` ya usa con el warning
  // de sub-unidades.
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    setPwCambiada(false);

    const quiereCambiarPw = Boolean(currentPw || newPw || confirmPw);
    if (quiereCambiarPw) {
      if (!currentPw) {
        setError('Escribe tu contraseña actual para poder cambiarla.');
        return;
      }
      if (newPw !== confirmPw) {
        setError('Las contraseñas nuevas no coinciden.');
        return;
      }
      if (!newPw) {
        setError('Escribe la contraseña nueva.');
        return;
      }
    }

    // Los datos personales son edición local (no hay PATCH /users/me todavía).
    updateProfile({ name, phone });
    setSaved(true);

    if (!quiereCambiarPw) {
      setTimeout(() => setSaved(false), 3000);
      return;
    }

    setGuardando(true);
    try {
      // Los requisitos reales los impone el SERVIDOR (política de contraseñas
      // de Identity Platform). No se replican aquí: un mínimo hardcodeado
      // mentiría en cuanto la política cambie.
      await cambiarContrasena(currentPw, newPw);
      setPwCambiada(true);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      setError(mensajeDeErrorDeContrasena(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mi Perfil" size="md">
      <form onSubmit={handleSave} className="my-profile-form" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Avatar & Role Badge */}
        <div className="my-profile-form__header">
          <div className="my-profile-form__avatar">
            {name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="my-profile-form__role-label">
              {ROLE_LABELS[user.role] ?? user.role}
            </p>
            <p className="my-profile-form__id">ID: {user.id}</p>
          </div>
        </div>

        {/* Divider */}
        <div className="my-profile-form__section-title">
          <User size={15} />
          Datos personales
        </div>

        <Input
          id="profile-name"
          label="Nombre completo"
          value={name}
          onChange={e => setName(e.target.value)}
          leftIcon={<User size={15} />}
          required
        />
        {/* Solo lectura, a propósito. No existe `PATCH /users/me`: hasta hoy
            este campo se podía editar y el cambio moría en el estado local.
            Y desde que la credencial la entrega un correo, la dirección es la
            LLAVE de la cuenta — cambiarla de mentira es peor que no ofrecerlo.
            El cambio real exige `verifyBeforeUpdateEmail` + endpoint; anotado
            en docs/frontend/PENDIENTES.md. */}
        <Input
          id="profile-email"
          label="Correo electrónico"
          type="email"
          value={email}
          leftIcon={<Mail size={15} />}
          readOnly
          disabled
        />
        <Input
          id="profile-phone"
          label="Teléfono"
          type="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          leftIcon={<Phone size={15} />}
          placeholder="+57 300 000 0000"
        />

        {/* Divider */}
        <div className="my-profile-form__section-title">
          <Lock size={15} />
          Cambiar contraseña
          <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>(opcional)</span>
        </div>

        <Input
          id="profile-current-pw"
          label="Contraseña actual"
          type={showPw ? 'text' : 'password'}
          value={currentPw}
          onChange={e => setCurrentPw(e.target.value)}
          leftIcon={<Lock size={15} />}
          placeholder="••••••••"
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.875rem' }}>
          <Input
            id="profile-new-pw"
            label="Nueva contraseña"
            type={showPw ? 'text' : 'password'}
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            leftIcon={<Lock size={15} />}
            placeholder="••••••••"
          />
          <Input
            id="profile-confirm-pw"
            label="Confirmar contraseña"
            type={showPw ? 'text' : 'password'}
            value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)}
            leftIcon={<Lock size={15} />}
            placeholder="••••••••"
          />
        </div>

        <button
          type="button"
          className="login-page__show-pass"
          onClick={() => setShowPw(s => !s)}
          style={{ alignSelf: 'flex-start' }}
        >
          {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
          {showPw ? 'Ocultar' : 'Mostrar'} contraseñas
        </button>

        {/* Error */}
        {error && (
          <div className="login-page__error" role="alert">{error}</div>
        )}

        {/* Success */}
        {saved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 'var(--radius-lg)', color: '#10B981', fontSize: '13px', fontWeight: 600 }}>
            <CheckCircle2 size={16} />
            Perfil actualizado con éxito.
          </div>
        )}

        {pwCambiada && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 'var(--radius-lg)', color: '#10B981', fontSize: '13px', fontWeight: 600 }}>
            <CheckCircle2 size={16} />
            Tu contraseña quedó cambiada.
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
          <Button variant="ghost" size="sm" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" type="submit" isLoading={guardando}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Modal>
  );
}
